import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '../stores/authStore'

const sanitizeMerchantErrorMessage = (message: string) => {
  const sanitized = message
    .replace(/\bGSalary\s+error:\s*/gi, '')
    .replace(/\bGSalary\b:?\s*/gi, '')
    .trim()

  return sanitized || '請求失敗'
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    public readonly code?: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response.data
  },
  async (error: AxiosError<{ code: number; message: string }>) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout()
      window.location.replace('/')
    }

    let payload = error.response?.data
    if (payload instanceof Blob) {
      payload = await payload.text().then((text) => JSON.parse(text)).catch(() => undefined)
    }
    const message = sanitizeMerchantErrorMessage(payload?.message || error.message || '請求失敗')
    return Promise.reject(new ApiError(message, error.response?.status, payload?.code))
  }
)

export interface ApiResponse<T> {
  code: number
  message: string
  data: T
}

export interface PageResponse<T> {
  items: T[]
  pagination: {
    page: number
    size: number
    total: number
    totalPages: number
  }
}

export default api
